import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, StageChip } from "../../dashboard/primitives";
import { subscribeAllUsers } from "../../projects/api";
import { useAuth } from "../../auth/AuthContext";
import { isSuperAdminProfile } from "../../auth/ProtectedRoute";
import { api } from "../../lib/api";

const ROLES = ["customer", "admin", "superAdmin"];

const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
};

const UsersSection = () => {
  const { user: me, profile: myProfile } = useAuth();
  const superAdmin = isSuperAdminProfile(myProfile);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => subscribeAllUsers(setUsers), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const toMs = (ts) => (ts?.toDate ? ts.toDate().getTime() : 0);
    return users
      .filter(
        (u) =>
          !q ||
          (u.displayName || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q)
      )
      .sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
  }, [users, query]);

  const patchUser = async (uid, body) => {
    setBusyId(uid);
    setError(null);
    try {
      await api.put("/api/auth/update-role", { uid, ...body });
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card
      title="Users"
      action={
        <input
          className="fw-input"
          style={{ maxWidth: 220, padding: "8px 12px", fontSize: 13 }}
          placeholder="Search name or email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      }
    >
      {error && (
        <div style={{ marginBottom: 12, fontSize: 12, color: "var(--fw-red)", fontFamily: "var(--fw-mono)" }}>
          {error}
        </div>
      )}

      {filtered.length === 0 ? (
        <Empty>No users match</Empty>
      ) : (
        filtered.map((u) => {
          const role = u.role || (u.isAdmin ? "admin" : "customer");
          const isMe = u.uid === me?.uid || u.id === me?.uid;
          const busy = busyId === (u.uid || u.id);
          return (
            <div
              className="fw-stack-sm"
              key={u.id}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1.4fr 1fr 1fr auto",
                gap: 12,
                alignItems: "center",
                padding: "12px 0",
                borderBottom: "1px solid var(--fw-line)",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {u.displayName || "—"}
                  {isMe && (
                    <span style={{ fontFamily: "var(--fw-mono)", fontSize: 10, color: "var(--fw-red)", marginLeft: 8 }}>
                      YOU
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>{u.email}</div>
              </div>

              <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                <div>{u.stripeCustomerId || "no stripe customer"}</div>
                <div>joined {fmtDate(u.createdAt)}</div>
              </div>

              <div>
                {superAdmin && !isMe ? (
                  <select
                    className="fw-input"
                    style={{ padding: "6px 8px", fontSize: 12 }}
                    value={role}
                    disabled={busy}
                    onChange={(e) => patchUser(u.uid || u.id, { role: e.target.value })}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                ) : (
                  <StageChip status={role} />
                )}
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "var(--fw-muted)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!u.isClient}
                  disabled={busy}
                  onChange={(e) => patchUser(u.uid || u.id, { isClient: e.target.checked })}
                />
                client
              </label>

              <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                {(u.uid || u.id).slice(0, 6)}
              </span>
            </div>
          );
        })
      )}
    </Card>
  );
};

export default UsersSection;
